// Thin, typed wrapper over libsodium-wrappers-sumo. Every higher-level crypto
// module goes through these helpers so the choice of primitive lives in one place.
// All asymmetric / password crypto runs HERE, client-side only.
import _sodium from "libsodium-wrappers-sumo";
import { utf8 } from "@shared/encoding.js";
import { ARGON2_OPSLIMIT, ARGON2_MEMLIMIT, KEY_BYTES, XNONCE_BYTES } from "@shared/constants.js";

export interface KeyPair {
  pub: Uint8Array;
  priv: Uint8Array;
}

type Sodium = typeof _sodium;
let s: Sodium | null = null;

/** Must be awaited once before any other crypto call (loads the WASM module). */
export async function initCrypto(): Promise<void> {
  if (s) return;
  await _sodium.ready;
  s = _sodium;
}

function S(): Sodium {
  if (!s) throw new Error("crypto not initialized - await initCrypto() first");
  return s;
}

export const randomBytes = (n: number): Uint8Array => S().randombytes_buf(n);

// ---- Asymmetric keys -------------------------------------------------------

export function genX25519(): KeyPair {
  const kp = S().crypto_box_keypair();
  return { pub: kp.publicKey, priv: kp.privateKey };
}

export function genEd25519(): KeyPair {
  const kp = S().crypto_sign_keypair();
  return { pub: kp.publicKey, priv: kp.privateKey };
}

export function ed25519FromSeed(seed: Uint8Array): KeyPair {
  const kp = S().crypto_sign_seed_keypair(seed);
  return { pub: kp.publicKey, priv: kp.privateKey };
}

/** X25519 Diffie-Hellman. Throws on a low-order/all-zero result (libsodium guard). */
export const dh = (priv: Uint8Array, pub: Uint8Array): Uint8Array => S().crypto_scalarmult(priv, pub);

export const sign = (msg: Uint8Array, priv: Uint8Array): Uint8Array => S().crypto_sign_detached(msg, priv);

export function verify(sig: Uint8Array, msg: Uint8Array, pub: Uint8Array): boolean {
  try {
    return S().crypto_sign_verify_detached(sig, msg, pub);
  } catch {
    return false; // malformed signature/key length -> not valid
  }
}

// ---- Hashing / KDF ---------------------------------------------------------

/** BLAKE2b. Keyed when `key` is supplied (a PRF); unkeyed otherwise. */
export const hash = (msg: Uint8Array, outLen = KEY_BYTES, key?: Uint8Array): Uint8Array =>
  S().crypto_generichash(outLen, msg, key ?? null);

/** Domain-separated key derivation: keyed BLAKE2b over a context string. */
export const kdf = (key: Uint8Array, ctx: string, outLen = KEY_BYTES): Uint8Array =>
  S().crypto_generichash(outLen, utf8(ctx), key);

// ---- Password stretching (Argon2id) ----------------------------------------

export const argon2id = (password: string, salt: Uint8Array, outLen = KEY_BYTES): Uint8Array =>
  S().crypto_pwhash(outLen, utf8(password), salt, ARGON2_OPSLIMIT, ARGON2_MEMLIMIT, S().crypto_pwhash_ALG_ARGON2ID13);

// ---- AEAD (XChaCha20-Poly1305 IETF) ----------------------------------------

export interface Sealed {
  nonce: Uint8Array;
  ct: Uint8Array;
}

export function aeadSeal(key: Uint8Array, plaintext: Uint8Array, ad: Uint8Array | null = null): Sealed {
  const nonce = randomBytes(XNONCE_BYTES);
  const ct = S().crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, ad, null, nonce, key);
  return { nonce, ct };
}

/** Decrypt; throws if authentication fails. */
export function aeadOpen(key: Uint8Array, nonce: Uint8Array, ct: Uint8Array, ad: Uint8Array | null = null): Uint8Array {
  return S().crypto_aead_xchacha20poly1305_ietf_decrypt(null, ct, ad, nonce, key);
}

/** Best-effort zeroing of sensitive buffers. Not a guarantee under a GC'd runtime. */
export const wipe = (b: Uint8Array): void => S().memzero(b);

/** Constant-time equality for secrets. */
export const equal = (a: Uint8Array, b: Uint8Array): boolean => a.length === b.length && S().memcmp(a, b);
